import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { UpdateNotificationsCommand } from '@/api/generated';
import {
  useContacts,
  useDashboard,
  useMembers,
  useNotifications,
  useProject,
  useShareLinks,
  useUpdateNotifications,
} from '@/api/hooks';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { SideNav, SideNavItem, SideNavSection } from '@/design-system/chrome/SideNav';
import { Icon, type IconName } from '@/design-system/icon/Icon';
import { Avatar } from '@/design-system/primitives/Avatar/Avatar';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { Switch } from '@/design-system/primitives/Switch/Switch';
import { DeleteAccountDialog, ExportAccountDialog } from './PrivacyDialogs';
import styles from './Settings.module.css';
import {
  InviteUserDialog,
  ProjectFieldDialog,
  type ProjectFieldKey,
  ShareLinkDialog,
} from './SettingsDialogs';

// BF-12 — Settings tab is now hydrated from the BF-5 read endpoints rather
// than constants. Each tab does its own `useQuery` so they hydrate lazily and
// a failure in one tab doesn't block the others.

interface NotifRow {
  id: string;
  label: string;
}

// 6 events × 1 channel (mail). Per the 2026-05-16 audit, only email
// delivery is in scope — the App / SMS columns were aspirational and got
// dropped to match operational reality (Resend is the only outbound
// transport).
const NOTIF_ROWS: readonly NotifRow[] = [
  { id: 'invoice-due', label: 'Factuur vervalt <7 dagen' },
  { id: 'legal-deadline', label: 'Wettelijke deadline <2 maand' },
  { id: 'decision-waiting', label: 'Wacht-op-mij beslissing' },
  { id: 'werfverslag', label: 'Werfverslag gepubliceerd' },
  { id: 'doc-uploaded', label: 'Document geüpload door anderen' },
];

const MAIL_CHANNEL = 'mail' as const;

// Defaults when the user has no persisted preferences yet. Server state
// (via `useNotifications`) overrides these on hydration.
const DEFAULT_MAIL_ENABLED: Record<string, boolean> = {
  'invoice-due': true,
  'legal-deadline': true,
  'decision-waiting': true,
  werfverslag: false,
  'doc-uploaded': false,
};

type TabId = 'project' | 'users' | 'notifications' | 'privacy';

const SIDE_NAV: Array<{ id: TabId; label: string; icon: IconName }> = [
  { id: 'project', label: 'Project', icon: 'home' },
  { id: 'users', label: 'Gebruikers & toegang', icon: 'users' },
  { id: 'notifications', label: 'Notificaties', icon: 'bell' },
  { id: 'privacy', label: 'Privacy & export', icon: 'shield' },
];

function NotifCell({ on, onChange, label }: { on: boolean; onChange: () => void; label: string }) {
  return (
    <div className={styles.notifCell}>
      <Switch checked={on} onCheckedChange={onChange} aria-label={label} />
    </div>
  );
}

/** Build the flat settings payload the PUT endpoint expects. */
function flatNotifSettings(enabled: Record<string, boolean>) {
  return NOTIF_ROWS.map((row) => ({
    channel: `${row.id}:${MAIL_CHANNEL}`,
    enabled: enabled[row.id] ?? false,
  }));
}

/** Pluck just the `<event>:mail` entries out of the persisted bag. */
function hydrateMailToggles(
  persisted: ReadonlyArray<{ channel: string; enabled: boolean }> | undefined,
): Record<string, boolean> {
  const out = { ...DEFAULT_MAIL_ENABLED };
  if (!persisted) return out;
  for (const { channel, enabled } of persisted) {
    const [eventId, ch] = channel.split(':');
    if (ch !== MAIL_CHANNEL) continue;
    if (eventId && eventId in out) out[eventId] = enabled;
  }
  return out;
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '';
  return d;
}

// OpenAPI codegen surfaces `decimal` as `number | string`. Coerce to number
// for math / formatting; treat invalid values as 0.
function asNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatBudget(amount: number | string | null | undefined): string {
  const n = asNumber(amount);
  if (n === 0) return '';
  return new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

export function Settings() {
  const [tab, setTab] = useState<TabId>('project');

  const dashboard = useDashboard();
  const project = useProject();
  const members = useMembers();
  const shareLinks = useShareLinks();
  const notifications = useNotifications();
  const contacts = useContacts({ pageSize: 100 });

  const projectName = project.data?.name ?? dashboard.data?.projectName ?? '—';
  const projectAddress = project.data?.address ?? dashboard.data?.projectAddress ?? '—';
  const currentPhase = dashboard.data?.currentPhase;

  // Project-field edit dialog: a single dialog reused for each of the five
  // editable project fields. `null` means closed.
  const [projectEdit, setProjectEdit] = useState<ProjectFieldKey | null>(null);

  // Standalone dialogs.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Notification toggle state. Hydrate from server when available; debounced
  // PUT syncs subsequent changes.
  const [mailEnabled, setMailEnabled] = useState<Record<string, boolean>>(DEFAULT_MAIL_ENABLED);
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (notifications.data) {
      setMailEnabled(hydrateMailToggles(notifications.data));
      hydratedRef.current = true;
    }
  }, [notifications.data]);

  const updateNotifs = useUpdateNotifications();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (!dirtyRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const body: UpdateNotificationsCommand = { settings: flatNotifSettings(mailEnabled) };
      updateNotifs.mutate(body);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [mailEnabled, updateNotifs]);

  const handleToggle = (rowId: string) => {
    dirtyRef.current = true;
    setMailEnabled((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  // Project field values feed into the edit dialog so it opens prefilled
  // with whatever the backend has persisted (BF-11).
  const projectFieldValues = useMemo(() => {
    const budget = asNumber(project.data?.budgetTotal);
    const offsets = project.data?.taskNotificationDefaultOffsetsDays?.map(Number) ?? [7, 1];
    return {
      name: projectName,
      address: projectAddress,
      startDate: formatDate(project.data?.startDate),
      endDate: formatDate(project.data?.endDate),
      budgetTotal: budget > 0 ? String(budget) : '',
      taskNotificationOffsets: offsets.join(', '),
      legalNotificationContact: project.data?.legalNotificationContactId ?? '',
    };
  }, [
    projectName,
    projectAddress,
    project.data?.startDate,
    project.data?.endDate,
    project.data?.budgetTotal,
    project.data?.taskNotificationDefaultOffsetsDays,
    project.data?.legalNotificationContactId,
  ]);

  // Resolve the legal-notification contact id to a display name for the
  // read-only row. Falls back to nothing if the contact list hasn't loaded
  // or the id no longer matches a known contact.
  const legalContactId = project.data?.legalNotificationContactId ?? '';
  const legalContact = (contacts.data?.items ?? []).find((c) => c.id === legalContactId);

  return (
    <AppFrame>
      <div className={`row ${styles.headerRow}`}>
        <div>
          <div className="eyebrow">Instellingen</div>
          <h1 className={styles.title}>Instellingen</h1>
        </div>
      </div>

      <div className={styles.body}>
        <SideNav ariaLabel="Instellingen secties">
          <SideNavSection>Secties</SideNavSection>
          {SIDE_NAV.map((n) => (
            <SideNavItem
              key={n.id}
              label={n.label}
              icon={n.icon}
              active={tab === n.id}
              onClick={() => setTab(n.id)}
              role="tab"
            />
          ))}
        </SideNav>

        <div className={`col gap-16 ${styles.content}`}>
          {tab === 'project' && (
            <Card className={styles.section}>
              <h2 className={styles.sectionTitle}>Project</h2>
              <p className={styles.sectionSub}>Basisgegevens en huidige fase.</p>
              <div className="col gap-14">
                <ProjectFieldRow
                  label="Projectnaam"
                  value={projectName}
                  onEdit={() => setProjectEdit('name')}
                />
                <ProjectFieldRow
                  label="Adres"
                  value={projectAddress}
                  onEdit={() => setProjectEdit('address')}
                />
                <ProjectFieldRow
                  label="Startdatum"
                  value={
                    projectFieldValues.startDate || (
                      <span className={styles.fieldHint}>klik om in te stellen</span>
                    )
                  }
                  onEdit={() => setProjectEdit('startDate')}
                />
                <ProjectFieldRow
                  label="Verwachte oplevering"
                  value={
                    projectFieldValues.endDate || (
                      <span className={styles.fieldHint}>klik om in te stellen</span>
                    )
                  }
                  onEdit={() => setProjectEdit('endDate')}
                />
                <ProjectFieldRow
                  label="Budget (totaal)"
                  value={
                    formatBudget(project.data?.budgetTotal) || (
                      <span className={styles.fieldHint}>klik om in te stellen</span>
                    )
                  }
                  onEdit={() => setProjectEdit('budgetTotal')}
                />
                <ProjectFieldRow
                  label="Notificaties standaard (dagen voor deadline)"
                  value={
                    projectFieldValues.taskNotificationOffsets || (
                      <span className={styles.fieldHint}>klik om in te stellen</span>
                    )
                  }
                  onEdit={() => setProjectEdit('taskNotificationOffsets')}
                />
                <ProjectFieldRow
                  label="Verantwoordelijke wettelijke notificaties"
                  value={
                    legalContact ? (
                      `${legalContact.name} — ${legalContact.role}`
                    ) : (
                      <span className={styles.fieldHint}>geen — klik om in te stellen</span>
                    )
                  }
                  onEdit={() => setProjectEdit('legalNotificationContact')}
                />
                <div className={`row ${styles.field}`}>
                  <span className={styles.fieldLabel}>Huidige fase</span>
                  <span className={`flex-1 ${styles.fieldValue}`}>
                    {currentPhase ? (
                      <Pill kind="warn">
                        {currentPhase.label}
                        {currentPhase.progress !== null && currentPhase.progress !== undefined
                          ? ` · ${Math.round(asNumber(currentPhase.progress) * 100)}%`
                          : ''}
                      </Pill>
                    ) : (
                      <span className={styles.fieldHint}>geen fase actief</span>
                    )}
                  </span>
                </div>
              </div>
            </Card>
          )}

          {tab === 'users' && (
            <Card className={styles.section}>
              <h2 className={styles.sectionTitle}>Gebruikers &amp; toegang</h2>
              <p className={styles.sectionSub}>Wie mag wat zien en bewerken.</p>
              <div className="col">
                {(members.data ?? []).map((m, i, arr) => (
                  <div
                    key={m.clerkUserId}
                    className={`row gap-12 ${styles.userRow} ${
                      i === arr.length - 1 ? styles.userRowLast : ''
                    }`}
                  >
                    <Avatar name={m.displayName} size={36} />
                    <div className="flex-1">
                      <div className={styles.userName}>{m.displayName}</div>
                      <div className={styles.userRole}>{m.email}</div>
                    </div>
                    <Pill
                      kind={m.role === 'owner' ? 'ok' : m.role === 'invited' ? 'warn' : 'info'}
                      dot={false}
                    >
                      {m.role === 'owner'
                        ? 'eigenaar — volledige toegang'
                        : m.role === 'invited'
                          ? 'uitgenodigd · wacht op aanmelding'
                          : 'lid'}
                    </Pill>
                  </div>
                ))}
                {members.data && members.data.length === 0 && (
                  <div className={styles.userRole}>Geen leden gevonden.</div>
                )}
              </div>
              <div className={`row gap-8 ${styles.userActions}`}>
                <Btn onClick={() => setInviteOpen(true)}>
                  <Icon name="plus" size={13} /> Persoon uitnodigen
                </Btn>
                <Btn variant="ghost" onClick={() => setShareOpen(true)}>
                  <Icon name="link" size={13} /> Read-only deellink maken
                </Btn>
              </div>
              {shareLinks.data && shareLinks.data.length > 0 && (
                <div className={`col gap-6 ${styles.userActions}`}>
                  <div className="eyebrow">Actieve deellinks</div>
                  {shareLinks.data.map((s) => (
                    <div key={s.id} className="row gap-8">
                      <span className="mono flex-1">{s.url}</span>
                      <span className={styles.fieldHint}>
                        {s.expiresAt ? `vervalt ${s.expiresAt.slice(0, 10)}` : 'permanent'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {tab === 'notifications' && (
            <Card className={styles.section}>
              <h2 className={styles.sectionTitle}>Notificaties</h2>
              <p className={styles.sectionSub}>
                Alle notificaties worden per email afgeleverd via no-reply@b-itsolutions.be.
              </p>
              <div className={styles.notifGrid}>
                <div />
                <div className={`eyebrow ${styles.notifHead}`}>Mail</div>
                {NOTIF_ROWS.map((r) => (
                  // Fragment so each row's label + cell are direct grid
                  // children — a wrapping div breaks the 2-column layout.
                  <Fragment key={r.id}>
                    <div className={styles.notifLabel}>{r.label}</div>
                    <NotifCell
                      on={mailEnabled[r.id] ?? false}
                      onChange={() => handleToggle(r.id)}
                      label={`${r.label} — mail`}
                    />
                  </Fragment>
                ))}
              </div>
            </Card>
          )}

          {tab === 'privacy' && (
            <Card className={styles.section}>
              <h2 className={styles.sectionTitle}>Privacy &amp; export</h2>
              <p className={styles.sectionSub}>
                Download je gegevens of verwijder je account onomkeerbaar.
              </p>
              <div className="col gap-10">
                <div className={`row ${styles.field}`}>
                  <span className={styles.fieldLabel}>Sub-processors</span>
                  <span className={`flex-1 ${styles.fieldValue}`}>
                    Clerk (auth) · Resend (mail) · Sentry (errors)
                  </span>
                </div>
                <div className="row gap-8">
                  <Btn onClick={() => setExportOpen(true)}>Download mijn data (ZIP)</Btn>
                  <Btn variant="ghost" onClick={() => setDeleteOpen(true)}>
                    Verwijder account
                  </Btn>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {projectEdit !== null && (
        <ProjectFieldDialog
          open={projectEdit !== null}
          onOpenChange={(o) => !o && setProjectEdit(null)}
          field={projectEdit}
          initialValue={projectFieldValues[projectEdit]}
        />
      )}
      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <ShareLinkDialog open={shareOpen} onOpenChange={setShareOpen} />
      <ExportAccountDialog open={exportOpen} onOpenChange={setExportOpen} />
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />
    </AppFrame>
  );
}

function ProjectFieldRow({
  label,
  value,
  onEdit,
}: {
  label: string;
  value: React.ReactNode;
  onEdit: () => void;
}) {
  return (
    <div className={`row ${styles.field}`}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={`flex-1 ${styles.fieldValue}`}>{value}</span>
      <Btn
        variant="ghost"
        className={styles.fieldBtn}
        onClick={onEdit}
        aria-label={`Wijzig ${label}`}
      >
        wijzig
      </Btn>
    </div>
  );
}
